import {RefObject, useEffect, useState} from 'react';

export const useOnScreen = (
  ref: RefObject<HTMLElement>,
  initialState: boolean | undefined = false,
  options?: IntersectionObserverInit,
) => {
  const [isOnScreen, setIsOnScreen] = useState(initialState);

  useEffect(() => {
    const view = ref.current;

    if (!window.IntersectionObserver || !view) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsOnScreen(entry.isIntersecting),
      options,
    );

    observer.observe(view);

    return () => {
      observer.disconnect();
    };
  }, [options, ref]);

  return isOnScreen;
};

export const useOnScreenOnce = ({
  ref,
  intersectionThreshold,
  callback,
}: {
  ref: RefObject<HTMLElement>;
  intersectionThreshold: number;
  callback: () => void;
}) => {
  useEffect(() => {
    const view = ref.current;

    if (!window.IntersectionObserver || !view) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          callback();
          observer.disconnect();
        }
      },
      {
        threshold: intersectionThreshold,
      },
    );

    observer.observe(view);

    return () => {
      observer.disconnect();
    };
  }, [callback, intersectionThreshold, ref]);
};

export const useOnScreenById = ({
  elementId,
  initialState = false,
  options,
  enabled = true,
}: {
  elementId: string;
  initialState?: boolean;
  options?: IntersectionObserverInit;
  enabled?: boolean;
}) => {
  const [isOnScreen, setIsOnScreen] = useState(initialState);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const view = document.getElementById(elementId);

    if (!window.IntersectionObserver || !view) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsOnScreen(entry.isIntersecting),
      options,
    );

    observer.observe(view);

    return () => {
      observer.disconnect();
    };
  }, [options, elementId, enabled]);

  return isOnScreen;
};
